import React, { useMemo, useState } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useChartCumulativeSpend } from '../../../hooks/useChartData';
import ChartCard from '../wrappers/ChartCard';
import TransactionDrilldownModal from '../wrappers/TransactionDrilldownModal';
import nivoCustomTheme from '../theme/nivoCustomTheme';
import useResponsive from '../../../hooks/useResponsive';

const TABLEAU10 = ['#4e79a7','#f28e2c','#e15759','#76b7b2','#59a14f','#edc949','#af7aa1','#ff9da7','#9c755f','#bab0ab'];

const formatCLP = (v) => `$${Math.round(v).toLocaleString('es-CL')}`;

function CustomLegend({ keys, colors }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', mt: 1.5, px: 0.5 }}>
      {keys.map((key, i) => (
        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colors?.[i] ?? TABLEAU10[i % 10], flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>{key}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function CumulativeStackedBarChart({ dateFrom, dateTo, categoryIds }) {
  const { isMobile } = useResponsive();
  const { data, loading, error } = useChartCumulativeSpend({ dateFrom, dateTo, categoryIds });
  const [drilldown, setDrilldown] = useState(null);

  const { chartData, keys, colors } = useMemo(() => {
    if (!data?.chartCumulativeSpend) return { chartData: [], keys: [], colors: [] };
    const { keys, colors, rows } = data.chartCumulativeSpend;
    const chartData = rows.map((row) => ({
      date: row.date,
      ...JSON.parse(row.jsonValues),
    }));
    return { chartData, keys, colors };
  }, [data]);

  const handleClick = (bar) => {
    setDrilldown({
      categoryName: bar.id,
      dateFrom: dateFrom ? dateFrom.format('YYYY-MM-DD') : null,
      dateTo: bar.indexValue,
    });
  };

  return (
    <>
      <ChartCard
        title="Acumulado por Día y Categoría"
        subtitle="Suma acumulada de gastos diarios — vista apilada"
        loading={loading && !data}
        error={error}
        height={320}
        footer={<CustomLegend keys={keys} colors={colors} />}
      >
        <ResponsiveBar
          data={chartData}
          keys={keys}
          indexBy="date"
          groupMode="stacked"
          theme={nivoCustomTheme}
          colors={colors}
          margin={isMobile
            ? { top: 10, right: 10, bottom: 60, left: 50 }
            : { top: 10, right: 20, bottom: 60, left: 70 }
          }
          padding={0.2}
          axisBottom={{
            tickSize: 0,
            tickPadding: 6,
            tickRotation: -45,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 8,
            format: (v) => `$${(v / 1000).toFixed(0)}k`,
          }}
          enableGridY={true}
          enableGridX={false}
          enableLabel={false}
          onClick={handleClick}
          tooltip={({ id, value, indexValue }) => (
            <div style={{ background: '#fff', padding: '8px 12px', borderRadius: 8, border: '1px solid #E8E5DF', boxShadow: '0 4px 20px rgba(0,0,0,0.10)', fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{indexValue}</div>
              <div>{id}: {formatCLP(value)}</div>
            </div>
          )}
          legends={[]}
          animate={true}
          motionConfig="gentle"
        />
      </ChartCard>

      {drilldown && (
        <TransactionDrilldownModal
          open={!!drilldown}
          onClose={() => setDrilldown(null)}
          categoryName={drilldown.categoryName}
          dateFrom={drilldown.dateFrom}
          dateTo={drilldown.dateTo}
        />
      )}
    </>
  );
}
